/* Product tabs bento: nav + panel switching for
   `sections/product-tabs-bento.liquid` (full rewrite, 2026-09-24, replacing
   the button-moving script for the removed `tab-item` / `tab-item-list`
   blocks — see `docs/product-tabs-bento.md`).

   Local `tab` blocks render their own nav button (`[data-m-tabs-button]`,
   inside `[data-m-tabs-nav]`) and their own panel
   (`.m-product-tabs-bento__panel`) directly from Liquid, in their final DOM
   position — `section.blocks` is readable straight from the section
   template, so nav and panels come out of one loop and there is nothing to
   move at runtime. The first panel renders visible server-side, the rest
   `hidden`, and the first button already carries `aria-selected="true"`;
   this file only keeps that state in sync after user or editor interaction.

   `activate()` reads a button's siblings straight from its own
   `[data-m-tabs-nav]` and pairs each one with its panel through
   `aria-controls` <-> `id` on every call, so there is no tab registry to
   keep in sync with the DOM — a fresh read is correct even after the editor
   swaps in new markup. The only state kept between calls is one
   AbortController per root, keyed by section id, so
   `shopify:section:unload` can remove exactly that instance's listeners.

   Both ids carry the section id as well as the block id: block ids are only
   unique within one section, and a duplicated section keeps them (two
   sections on the home page both had `tab_1` … `tab_5`). The panel is also
   looked up inside the tab's own section, never document-wide, so a clash
   can at worst break pairing inside one section, not switch another one.

   From 1024px the nav wraps; below it the nav is one row that scrolls
   sideways (assets/product-tabs-bento.css). Touch pans it natively; a mouse
   has no click-drag on a scroll container, so `dragNav()` adds one, the
   same way the product carousel does (assets/product-list-bento.js).
   Activating a tab whose neighbour is cut by the row's edge scrolls the row
   just far enough to show that neighbour (`revealNeighbours()`), both
   ways: the next tab is always one tap away without a swipe.

   Hidden panels are `display: none`, so the browser does not start their lazy
   card images until the tab is opened, and the shopper watched empty cards
   fill in. `warmPanels()` runs once, when the section is near the viewport
   and the browser is idle: in every hidden panel the first cards — as many as
   the open panel shows in its first row, plus the peeking one — get their
   main image switched to eager. The rest of an opened row is the carousel's
   job (`warmAhead()`, assets/product-list-bento.js).

   A panel that goes from hidden to shown gets `.is-entering` for the length
   of its cards' entry animation (assets/product-tabs-bento.css); the first
   panel, visible from the server, never does. */
(function () {
  function each(list, callback) {
    Array.prototype.forEach.call(list, callback);
  }

  var controllers = {};

  function buttons(nav) {
    return nav.querySelectorAll('[data-m-tabs-button]');
  }

  /* The buttons the arrow keys move between: a `hidden` one (the recently
     viewed tab before it has cards) cannot take focus. */
  function focusable(nav) {
    return nav.querySelectorAll('[data-m-tabs-button]:not([hidden])');
  }

  /* Activates `button`'s tab: `aria-selected` / `tabindex` on every button
     in its nav, `hidden` on every panel reached through that button's own
     `aria-controls`, searched in the same section only. */
  function activate(button) {
    var nav = button.closest('[data-m-tabs-nav]');
    if (!nav) return;
    var root = nav.closest('.m-product-tabs-bento');

    each(buttons(nav), function (other) {
      var active = other === button;
      other.setAttribute('aria-selected', active ? 'true' : 'false');
      other.setAttribute('tabindex', active ? '0' : '-1');

      var id = other.getAttribute('aria-controls');
      var panel = id && root ? root.querySelector('[id="' + id + '"]') : null;
      if (!panel) return;
      if (active) {
        var opening = panel.hasAttribute('hidden');
        panel.removeAttribute('hidden');
        if (opening) enter(panel);
      } else {
        panel.setAttribute('hidden', '');
        panel.classList.remove('is-entering');
      }
    });

    revealNeighbours(nav, button);
  }

  /* On a row that scrolls (below 1024px), after a tab is activated: when the
     next visible tab sticks out past the right edge, the row scrolls left
     until it is whole; when the previous one sticks out past the left edge,
     right until that one is. "The edge" is inside the row's inline padding,
     the gap it keeps at both scroll ends. With neither neighbour cut (or no
     neighbour, at the ends) the tab itself is kept whole; otherwise the row
     stays put, so tapping a tab in the middle never moves it. */
  function revealNeighbours(nav, button) {
    if (nav.scrollWidth <= nav.clientWidth) return;
    var list = Array.prototype.slice.call(focusable(nav));
    var index = list.indexOf(button);
    if (index === -1) return;

    var style = window.getComputedStyle(nav);
    var box = nav.getBoundingClientRect();
    var left = box.left + (parseFloat(style.paddingLeft) || 0);
    var right = box.right - (parseFloat(style.paddingRight) || 0);

    function shift(element) {
      if (!element) return 0;
      var rect = element.getBoundingClientRect();
      if (rect.right > right) return rect.right - right;
      if (rect.left < left) return rect.left - left;
      return 0;
    }

    var next = list[index + 1];
    var prev = list[index - 1];
    var delta = next ? Math.max(0, shift(next)) : 0;
    if (!delta && prev) delta = Math.min(0, shift(prev));
    if (!delta) delta = shift(button);
    if (!delta) return;

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    nav.scrollBy({ left: delta, behavior: reduce ? 'auto' : 'smooth' });
  }

  /* Restarts the entry animation: the class comes off, a forced style read
     lets the browser see the change, then it goes back on. Taken off again
     once the last delayed card has finished (0.25s delay + 0.5s run). */
  var enterTimers = new WeakMap();
  function enter(panel) {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    panel.classList.remove('is-entering');
    void panel.offsetWidth;
    panel.classList.add('is-entering');
    window.clearTimeout(enterTimers.get(panel));
    enterTimers.set(panel, window.setTimeout(function () {
      panel.classList.remove('is-entering');
    }, 900));
  }

  /* Cards the open panel shows at once: the items on its first row whose
     left edge is inside the panel, plus one for the card peeking in. A grid
     panel counts its first row only; its later rows load as the page scrolls. */
  function firstRowCount(panel) {
    var panelRect = panel.getBoundingClientRect();
    var list = panel.querySelectorAll('.m-bento-product-list__item');
    if (!list.length || !panelRect.width) return 0;
    var top = list[0].getBoundingClientRect().top;
    var count = 0;
    each(list, function (item) {
      var rect = item.getBoundingClientRect();
      if (Math.abs(rect.top - top) < 1 && rect.left < panelRect.right) count++;
    });
    return count + 1;
  }

  function warmPanels(root) {
    var panels = root.querySelectorAll('.m-product-tabs-bento__panel');
    var count = 0;
    each(panels, function (panel) {
      if (!panel.hidden) count = firstRowCount(panel);
    });
    if (!count) return;
    each(panels, function (panel) {
      if (!panel.hidden) return;
      var list = panel.querySelectorAll('.m-bento-product-list__item');
      for (var i = 0; i < list.length && i < count; i++) {
        each(list[i].querySelectorAll('img[loading="lazy"]'), function (img) {
          if (img.closest('.m-bcard__media-hover')) return;
          img.loading = 'eager';
        });
      }
    });
  }

  /* Waits for the section to come within a screen of the viewport, then for
     an idle moment, so the warm-up never competes with the images the
     shopper is looking at. */
  function scheduleWarm(root) {
    var run = function () {
      if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(function () { warmPanels(root); }, { timeout: 2000 });
      else window.setTimeout(function () { warmPanels(root); }, 200);
    };
    if (typeof IntersectionObserver !== 'function') {
      run();
      return null;
    }
    var observer = new IntersectionObserver(
      function (entries) {
        if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
        observer.disconnect();
        run();
      },
      { rootMargin: '100% 0px' }
    );
    observer.observe(root);
    return observer;
  }

  function focusAt(nav, index) {
    var list = focusable(nav);
    var count = list.length;
    if (!count) return;
    list[((index % count) + count) % count].focus();
  }

  function indexOf(nav, button) {
    return Array.prototype.indexOf.call(focusable(nav), button);
  }

  function sectionIdFor(nav) {
    var section = nav.closest('[id^="m-section-"]');
    return section ? section.id.replace(/^m-section-/, '') : null;
  }

  /* Mouse drag on the scrolling tab row. It starts on a tab too - the row
     is all tabs. Nothing is taken over until the pointer has moved past
     DRAG_SLOP, so a plain click still activates the tab; then the pointer is
     captured and the one click that follows the drag is swallowed, so the
     tab under the pointer does not switch. A row that fits (every width from
     1024px, where it wraps) has nothing to scroll and never starts a drag. */
  var DRAG_SLOP = 4;
  function dragNav(nav, options) {
    var drag = null;

    nav.addEventListener(
      'pointerdown',
      function (event) {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (nav.scrollWidth <= nav.clientWidth) return;
        drag = { id: event.pointerId, x: event.clientX, startLeft: nav.scrollLeft, moved: 0, active: false };
      },
      options
    );

    nav.addEventListener(
      'pointermove',
      function (event) {
        if (!drag || event.pointerId !== drag.id) return;
        var dx = event.clientX - drag.x;
        if (Math.abs(dx) > drag.moved) drag.moved = Math.abs(dx);
        if (!drag.active) {
          if (drag.moved <= DRAG_SLOP) return;
          drag.active = true;
          nav.classList.add('is-dragging');
          try {
            nav.setPointerCapture(event.pointerId);
          } catch (error) {
            /* Not capturable (a synthetic event): the move events still work. */
          }
        }
        nav.scrollLeft = drag.startLeft - dx;
        event.preventDefault();
      },
      options
    );

    var end = function (event) {
      if (!drag || event.pointerId !== drag.id) return;
      var active = drag.active;
      drag = null;
      if (!active) return;
      nav.classList.remove('is-dragging');
      try {
        nav.releasePointerCapture(event.pointerId);
      } catch (error) {
        /* Already released with the pointer. */
      }
      var swallow = function (clickEvent) {
        nav.removeEventListener('click', swallow, true);
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
      };
      nav.addEventListener('click', swallow, true);
      /* The click, if any, comes with this same pointerup; one released
         outside the row brings none, and the swallow must not wait for the
         next real click. */
      window.setTimeout(function () {
        nav.removeEventListener('click', swallow, true);
      }, 0);
    };
    nav.addEventListener('pointerup', end, options);
    nav.addEventListener('pointercancel', end, options);
  }

  function initNav(nav) {
    /* The editor re-renders the section into a fresh root, so the element
       itself is the guard: a fresh element has no marker and gets bound
       once. */
    if (nav.dataset.mTabsInit === 'true') return;
    nav.dataset.mTabsInit = 'true';

    var list = buttons(nav);
    if (!list.length) return;

    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var options = controller ? { signal: controller.signal } : false;

    each(list, function (button) {
      button.addEventListener(
        'click',
        function () {
          activate(button);
        },
        options
      );

      /* Roving tabindex: the arrows only move focus (wrapping at the ends),
         Enter/Space activates whatever is focused. `tabindex` itself is
         written by `activate()`, so the nav stays a single Tab stop. */
      button.addEventListener(
        'keydown',
        function (event) {
          var key = event.key;
          var index = indexOf(nav, button);
          if (key === 'ArrowLeft' || key === 'ArrowUp') {
            event.preventDefault();
            focusAt(nav, index - 1);
          } else if (key === 'ArrowRight' || key === 'ArrowDown') {
            event.preventDefault();
            focusAt(nav, index + 1);
          } else if (key === 'Home') {
            event.preventDefault();
            focusAt(nav, 0);
          } else if (key === 'End') {
            event.preventDefault();
            focusAt(nav, -1);
          } else if (key === 'Enter' || key === ' ') {
            /* A native button would dispatch its own click for both keys;
               activating here and cancelling the default keeps it to one
               run and stops Space from scrolling the page. */
            event.preventDefault();
            activate(button);
          }
        },
        options
      );
    });

    dragNav(nav, options);

    var root = nav.closest('.m-product-tabs-bento');
    var warmObserver = root ? scheduleWarm(root) : null;
    if (warmObserver && controller) {
      controller.signal.addEventListener('abort', function () {
        warmObserver.disconnect();
      });
    }

    var sectionId = sectionIdFor(nav);
    if (sectionId) controllers[sectionId] = controller;
  }

  function boot() {
    each(document.querySelectorAll('[data-m-tabs-nav]'), initNav);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* The editor re-renders the whole section into a fresh root (no
     `data-m-tabs-init` marker), so `boot()` re-runs `initNav` on it
     unguarded, same as first load. */
  document.addEventListener('shopify:section:load', boot);

  /* Removes exactly the unloaded instance's listeners — every other root's
     controller is untouched. A section without a nav (no tab blocks) never
     registered a controller, so this is a no-op for it. */
  document.addEventListener('shopify:section:unload', function (event) {
    var sectionId = event && event.detail && event.detail.sectionId;
    if (!sectionId || !controllers[sectionId]) return;
    controllers[sectionId].abort();
    delete controllers[sectionId];
  });

  /* Selecting a block in the editor sidebar selects the tab's button
     itself — `block.shopify_attributes` sits directly on it, and a `tab`
     block has no nested blocks of its own (its product list comes from its
     own settings, not from a child block), so `event.target` is always the
     button or something it contains. */
  document.addEventListener('shopify:block:select', function (event) {
    var target = event.target;
    var button = target && typeof target.closest === 'function' ? target.closest('[data-m-tabs-button]') : null;
    if (!button) return;
    activate(button);
  });
})();
